import axios from 'axios'
import { buildApiUrl, getApiOrigin } from '@/utils/apiBase'

const API_BASE_URL = getApiOrigin()

// Создаем инстанс axios для Organizations API
const organizationsApi = axios.create({
    baseURL: `${API_BASE_URL}/api/organizations/`,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

// Helper function to get CSRF token from cookie
function getCookie(name) {
    let cookieValue = null
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';')
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim()
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
                break
            }
        }
    }
    return cookieValue
}

// Интерсепторы
organizationsApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
            config.headers.Authorization = `Token ${token}`
        }
        const csrfToken = getCookie('csrftoken')
        if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken
        }
        return config
    },
    (error) => Promise.reject(error)
)

organizationsApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('access_token')
            window.location.href = '/login'
        }
        return Promise.reject(error)
    }
)

export default {
    // === ОРГАНИЗАЦИИ (Staff CRUD) ===

    // Получить список организаций
    async getOrganizations(params = {}) {
        const response = await organizationsApi.get('organizations/', { params })
        return response.data
    },

    // Получить детали организации
    async getOrganization(id) {
        const response = await organizationsApi.get(`organizations/${id}/`)
        return response.data
    },

    // Создать организацию
    async createOrganization(data) {
        const response = await organizationsApi.post('organizations/', data)
        return response.data
    },

    // Обновить организацию
    async updateOrganization(id, data) {
        const response = await organizationsApi.patch(`organizations/${id}/`, data)
        return response.data
    },

    // Деактивировать организацию
    async deleteOrganization(id) {
        return organizationsApi.delete(`organizations/${id}/`)
    },

    // === ПОДРАЗДЕЛЕНИЯ ===

    // Получить подразделения организации
    async getSubdivisions(organizationId) {
        const response = await organizationsApi.get(`organizations/${organizationId}/subdivisions/`)
        return response.data
    },

    // Добавить подразделение
    async addSubdivision(organizationId, data) {
        const response = await organizationsApi.post(`organizations/${organizationId}/add_subdivision/`, data)
        return response.data
    },

    // Обновить подразделение
    async updateSubdivision(organizationId, subdivisionId, data) {
        const response = await organizationsApi.patch(`organizations/${organizationId}/subdivisions/${subdivisionId}/`, data)
        return response.data
    },

    // Удалить подразделение
    async deleteSubdivision(organizationId, subdivisionId) {
        return organizationsApi.delete(`organizations/${organizationId}/subdivisions/${subdivisionId}/`)
    },

    // === ВРАЧИ ОРГАНИЗАЦИИ ===

    // Получить врачей организации
    async getOrganizationDoctors(organizationId) {
        const response = await organizationsApi.get(`organizations/${organizationId}/doctors/`)
        return response.data
    },

    // Добавить врача в организацию
    async addDoctorToOrganization(data) {
        const response = await organizationsApi.post('doctor-organizations/', data)
        return response.data
    },

    // Удалить врача из организации  
    async removeDoctorFromOrganization(membershipId) {
        return organizationsApi.delete(`doctor-organizations/${membershipId}/`)
    },

    // === ПАЦИЕНТЫ ОРГАНИЗАЦИИ ===

    // Получить пациентов организации
    async getOrganizationPatients(organizationId, params = {}) {
        const response = await organizationsApi.get(`organizations/${organizationId}/patients/`, { params })
        return response.data
    },

    // === ПУБЛИЧНЫЙ API (для mobile) ===

    // Получить список организаций для выбора (public)
    async getPublicOrganizations(params = {}) {
        const response = await organizationsApi.get('public/', { params })
        return response.data
    },

    // === ВРАЧ — МОИ ОРГАНИЗАЦИИ ===

    // Получить организации текущего врача
    async getDoctorOrganizations() {
        const response = await organizationsApi.get('doctor/organization-patients/my_organizations/')
        return response.data
    },

    // Получить пациентов организаций врача
    async getDoctorOrganizationPatients(params = {}) {
        const response = await organizationsApi.get('doctor/organization-patients/', { params })
        return response.data
    },

    // === ЗАФИКСИРОВАННЫЕ ЛИЦА (ELSYS) ===

    // Получить список зафиксированных лиц
    async getDetectedFaces(params = {}) {
        const response = await organizationsApi.get('doctor/detected-faces/', { params })
        return response.data
    },

    // Получить статистику по зафиксированным лицам
    async getDetectedFaceStatistics() {
        const response = await organizationsApi.get('doctor/detected-faces/statistics/')
        return response.data
    },

    // Идентифицировать лицо как пациента
    async identifyFace(faceId, patientId) {
        const response = await organizationsApi.post(`doctor/detected-faces/${faceId}/identify/`, {
            patient_id: patientId
        })
        return response.data
    },

    // Получить эмоциональные показатели пациентов
    async getEmotionalReadings(params = {}) {
        const response = await organizationsApi.get('doctor/emotional-readings/', { params })
        return response.data
    },

    // Сканировать папку ELSYS и обработать новые файлы
    async scanElsysFolder() {
        const response = await organizationsApi.post('doctor/detected-faces/scan_folder/')
        return response.data
    },

    // === СВЯЗАННЫЕ ПОЛИКЛИНИКИ (Staff) ===

    // Получить связанные поликлиники организации
    async getLinkedClinics(organizationId) {
        const response = await organizationsApi.get(`organizations/${organizationId}/linked_clinics/`)
        return response.data
    },

    // Добавить связанную поликлинику
    async addLinkedClinic(organizationId, clinicId, doctorId = null) {
        const payload = { clinic_id: clinicId }
        if (doctorId) {
            payload.doctor_id = doctorId
        }
        const response = await organizationsApi.post(`organizations/${organizationId}/linked_clinics/`, payload)
        return response.data
    },

    // Удалить связь с поликлиникой
    async removeLinkedClinic(organizationId, linkId) {
        return organizationsApi.delete(`organizations/${organizationId}/linked_clinics/${linkId}/`)
    },

    // === ЗАПИСЬ К ВРАЧАМ ПОЛИКЛИНИК (Doctor) ===

    // Получить врачей связанных поликлиник
    async getLinkedClinicDoctors(params = {}) {
        const response = await organizationsApi.get('doctor/clinic-appointments/doctors/', { params })
        return response.data
    },

    // Получить свободные слоты врача
    async getClinicDoctorSlots(doctorId, params = {}) {
        const response = await organizationsApi.get(`doctor/clinic-appointments/doctor-slots/${doctorId}/`, { params })
        return response.data
    },

    // Записать пациента к врачу поликлиники
    async bookClinicAppointment(data) {
        const response = await organizationsApi.post('doctor/clinic-appointments/book/', data)
        return response.data
    },

    // === СПЕЦИАЛЬНЫЕ УЧРЕЖДЕНИЯ — КАБИНЕТ АДМИНИСТРАТОРА ===

    async getCorrectionalOverview() {
        const response = await organizationsApi.get('correctional/workspace/overview/')
        return response.data
    },

    async getCorrectionalPatients(params = {}) {
        const response = await organizationsApi.get('correctional/patients/', { params })
        return response.data
    },

    async createCorrectionalPatient(data) {
        const response = await organizationsApi.post('correctional/patients/', data)
        return response.data
    },

    async updateCorrectionalPatient(membershipId, data) {
        const response = await organizationsApi.patch(`correctional/patients/${membershipId}/`, data)
        return response.data
    },

    async dismissCorrectionalPatient(membershipId) {
        return organizationsApi.delete(`correctional/patients/${membershipId}/`)
    },

    async getCorrectionalDoctors() {
        const response = await organizationsApi.get('correctional/workspace/doctors/')
        return response.data
    },

    async getCorrectionalDoctorSlots(doctorId, params = {}) {
        const response = await organizationsApi.get(`correctional/workspace/doctor-slots/${doctorId}/`, { params })
        return response.data
    },

    async getCorrectionalAppointments(params = {}) {
        const response = await organizationsApi.get('correctional/appointments/', { params })
        return response.data
    },

    async bookCorrectionalAppointment(data) {
        const response = await organizationsApi.post('correctional/appointments/', data)
        return response.data
    },

    async cancelCorrectionalAppointment(appointmentId) {
        const response = await organizationsApi.post(`correctional/appointments/${appointmentId}/cancel/`)
        return response.data
    },

    async joinCorrectionalAppointment(appointmentId) {
        const response = await organizationsApi.post(`correctional/appointments/${appointmentId}/join/`)
        return response.data
    },

    async getCorrectionalTrackerTypes() {
        const response = await organizationsApi.get('correctional/workspace/tracker-types/')
        return response.data
    },

    async getCorrectionalTrackers(params = {}) {
        const response = await organizationsApi.get('correctional/trackers/', { params })
        return response.data
    },

    async createCorrectionalTracker(data) {
        const response = await organizationsApi.post('correctional/trackers/', data)
        return response.data
    },

    async updateCorrectionalTracker(entryId, data) {
        const response = await organizationsApi.patch(`correctional/trackers/${entryId}/`, data)
        return response.data
    },

    async deleteCorrectionalTracker(entryId) {
        return organizationsApi.delete(`correctional/trackers/${entryId}/`)
    },

    // === СПЕЦИАЛЬНЫЕ УЧРЕЖДЕНИЯ — КАБИНЕТ ВРАЧА ===

    async getDoctorCorrectionalInstitutions() {
        const response = await organizationsApi.get('doctor/correctional/institutions/')
        return response.data
    },

    async getDoctorCorrectionalPatients(params = {}) {
        const response = await organizationsApi.get('doctor/correctional/patients/', { params })
        return response.data
    },

    async getDoctorCorrectionalAppointments(params = {}) {
        const response = await organizationsApi.get('doctor/correctional/appointments/', { params })
        return response.data
    },

    // Получить причины визита (для выбора в модальном окне)
    async getVisitReasons(params = {}) {
        // Используем endpoint из AppointmentViewSet (через patient router)
        // Так как organizationsApi настроен на /api/organizations/, нам нужно использовать полный URL или другой инстанс
        // Но проще сделать запрос через тот же инстанс, но с правильным путем
        const response = await axios.get(buildApiUrl('/api/patient/appointments/visit_reasons/'), {
            params,
            headers: {
                'Authorization': `Token ${localStorage.getItem('access_token')}`
            }
        })
        return response.data
    },

    // === ТИПЫ ОРГАНИЗАЦИЙ ===
    organizationTypes: [
        { value: 'educational', label: 'Учебная', labelKz: 'Оқу' },
        { value: 'work', label: 'Рабочая', labelKz: 'Жұмыс' },
        { value: 'correctional', label: 'Специальное учреждение', labelKz: 'Арнайы мекеме' }
    ],

    getOrganizationTypeLabel(type, lang = 'ru') {
        const found = this.organizationTypes.find(t => t.value === type)
        return found ? (lang === 'kz' ? found.labelKz : found.label) : type
    }
}
